import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MarketingNav, Footer } from "@/components/Marketing";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr, mediaUrl } from "@/lib/api";
import { themeOf } from "@/lib/themes";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

export default function Templates() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/templates"); setTemplates(data); }
      catch (e) { toast.error(apiErr(e)); }
    })();
  }, []);

  const use = async (tpl) => {
    if (!user) { nav("/register"); return; }
    setBusy(tpl.id);
    try {
      const { data } = await api.post(`/timelines/from-template/${tpl.id}`);
      toast.success("Template added to your timelines");
      nav(`/editor/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };

  return (
    <div className="bg-background min-h-screen">
      <MarketingNav />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Templates</div>
        <h1 className="font-display text-5xl font-semibold tracking-tight text-foreground">Start from a beautiful design</h1>
        <p className="mt-3 text-muted-foreground max-w-lg">Polished, ready-made timelines with sample content. Make one yours in a click.</p>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((tpl, i) => {
            const t = themeOf(tpl.theme);
            return (
              <motion.div key={tpl.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="group rounded-2xl border border-border bg-card overflow-hidden transition-transform duration-300 hover:-translate-y-1">
                <div className="h-40 relative overflow-hidden" style={{ background: t.bgGradient }}>
                  {tpl.cover_image && <img src={mediaUrl(tpl.cover_image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80 transition-transform duration-500 group-hover:scale-105" loading="lazy" />}
                  <span className="absolute top-3 left-3 text-[11px] px-2 py-0.5 rounded-full glass font-medium" style={{ color: t.text }}>{tpl.category}</span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-lg font-semibold text-foreground">{tpl.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2">{tpl.description}</p>
                  <Button onClick={() => use(tpl)} disabled={busy === tpl.id} className="mt-4 w-full rounded-full" variant="outline" data-testid={`use-template-${tpl.id}`}>
                    {busy === tpl.id ? <Loader2 className="animate-spin" size={16} /> : "Use template"}
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
      <Footer />
    </div>
  );
}
